<template>
  <div class="min-h-screen bg-gray-50 font-body">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between flex-col md:flex-row gap-3">
          <div class="flex items-center space-x-4 space-x-reverse">
            <div class="relative">
              <img 
                src="@/assets/Logo.png" 
                alt="کبابی مارو" 
                class="navbar-logo w-12 h-12"
              />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-gray-800">مدیریت سفارشات</h1>
              <p class="text-sm text-gray-500">کبابی مارو</p>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
             <button 
               @click="goToAdminPanel"
               class="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
             >
               بازگشت به پنل
             </button>
             <button 
               @click="goToSectionTableManagement"
               class="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
             >
               تنظیمات
             </button>
             <button 
               @click="goToMenu"
               class="bg-brown-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brown-700 transition-colors"
             >
               مشاهده منو
             </button>
           </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="container mx-auto px-4 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-brown-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-20">
        <div class="text-red-600 text-lg mb-4">{{ error }}</div>
        <button 
          @click="loadSections"
          class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
        >
          تلاش مجدد
        </button>
      </div>

      <!-- Content -->
      <div v-else class="space-y-6">
        <!-- Section Tabs -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <div class="flex space-x-1 space-x-reverse p-1 overflow-x-auto whitespace-nowrap">
                         <!-- Pickup and Internal Orders Tab -->
            <button 
               @click="activeSection = pickupSection"
               :class="[
                 'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
                 activeSection?.id === pickupSection.id 
                   ? 'bg-blue-600 text-white' 
                   : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
               ]"
              class="shrink-0"
             >
               سفارشات تحویل و داخلی
               <span class="ml-2 text-xs opacity-75">
                 ({{ pickupSection.tables.length }})
               </span>
             </button>
            
            <!-- Regular Section Tabs -->
            <button 
              v-for="section in sections" 
              :key="section.id"
              @click="activeSection = section"
              :class="[
                'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
                activeSection?.id === section.id 
                  ? 'bg-brown-600 text-white' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              ]"
              class="shrink-0"
            >
              {{ section.name }}
              <span class="ml-2 text-xs opacity-75">
                ({{ section.tables.length }})
              </span>
            </button>
          </div>
        </div>

        <!-- Tables Grid -->
        <div v-if="activeSection && activeSection.tables.length > 0" class="space-y-4">
          <div class="flex justify-between items-center flex-col md:flex-row gap-3">
            <h2 class="text-2xl font-semibold text-gray-800">
              {{ activeSection.name }}
            </h2>
            <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
              <!-- Create New Pickup Order Button -->
              <button 
                v-if="activeSection.id === 'pickup'"
                @click="createNewPickupOrder"
                class="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                + سفارش تحویل جدید
              </button>
              
              <!-- Create Internal Order Button -->
              <button 
                @click="createInternalOrder"
                class="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                سفارش داخلی
              </button>
              <div class="text-sm text-gray-500">
                {{ activeSection.tables.length }} {{ activeSection.id === 'pickup' ? 'سفارش' : 'میز' }}
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                         <div 
               v-for="table in activeSection.tables" 
               :key="table.id"
               @click="openTableOrder(table)"
               class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group"
               :class="{
                 'border-2 border-green-500': table.hasOpenOrder && table.orderStatus === 0,
                 'border-2 border-orange-500': table.hasOpenOrder && table.orderStatus === 3,
                 'border border-gray-200': !table.hasOpenOrder
               }"

             >
              <div class="text-center">
                <div class="text-3xl mb-2 group-hover:scale-110 transition-transform">
                  🍽️
                </div>
                <h3 class="font-semibold text-gray-800 text-lg mb-1">
                  {{ table.name }}
                </h3>
                <p class="text-sm text-gray-500 mb-3">
                  {{ table.description }}
                </p>
                
                <!-- Status Indicator -->
                <div class="flex items-center justify-center space-x-2 space-x-reverse mb-3">
                  <div 
                    :class="[
                      'w-3 h-3 rounded-full',
                      table.hasOpenOrder && table.orderStatus === 0 ? 'bg-green-500' : '',
                      table.hasOpenOrder && table.orderStatus === 3 ? 'bg-orange-500' : '',
                      !table.hasOpenOrder ? 'bg-gray-300' : ''
                    ]"
                  ></div>
                  <span class="text-xs text-gray-600">
                    {{ table.hasOpenOrder && table.orderStatus === 0 ? 'فعال' : '' }}
                    {{ table.hasOpenOrder && table.orderStatus === 3 ? 'پرداخت نشده' : '' }}
                    {{ !table.hasOpenOrder ? 'خالی' : '' }}
                  </span>
                </div>

                <!-- Capacity -->
                <div class="text-xs text-gray-500 mb-2">
                  ظرفیت: {{ table.capacity }} نفر
                </div>

                <!-- Open Order Total -->
                <div v-if="table.hasOpenOrder && table.openOrderTotal" 
                     :class="[
                       'text-sm font-medium',
                       table.orderStatus === 0 ? 'text-green-600' : '',
                       table.orderStatus === 3 ? 'text-orange-600' : ''
                     ]">
                  {{ formatPrice(table.openOrderTotal) }}
                </div>
              </div>
            </div>
          </div>
        </div>

                 <!-- No Tables Message -->
         <div v-else-if="activeSection && activeSection.tables.length === 0" class="text-center py-20">
           <div class="text-gray-500 text-lg mb-4">
             {{ activeSection.id === 'pickup' ? 'هیچ سفارش تحویلی یا داخلی وجود ندارد' : 'هیچ میزی در این بخش وجود ندارد' }}
           </div>
           <div v-if="activeSection.id === 'pickup'" class="text-sm text-gray-400 mb-4">
             برای ایجاد سفارش جدید، از دکمه‌های زیر استفاده کنید
           </div>
           <div v-if="activeSection.id === 'pickup'" class="mt-4 space-x-3 space-x-reverse">
             <button 
               @click="createNewPickupOrder"
               class="bg-blue-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
             >
               + سفارش تحویل جدید
             </button>
             <button 
               @click="createInternalOrder"
               class="bg-purple-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
             >
               + سفارش داخلی جدید
             </button>
           </div>
         </div>
      </div>
    </div>

    <!-- Order Modal -->
    <OrderModal 
      v-if="showOrderModal"
      :order="currentOrder"
      :table="selectedTable"
      @close="closeOrderModal"
      @order-updated="handleOrderUpdated"
    />

    <!-- Employee Selection Modal -->
    <div v-if="showEmployeeSelectionModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">انتخاب کارمند</h3>
          <button 
            @click="closeEmployeeSelectionModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">کارمند:</label>
          <select 
            v-model="selectedEmployee"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">انتخاب کنید...</option>
            <option 
              v-for="employee in employees" 
              :key="employee.id" 
              :value="employee"
            >
              {{ employee.fullName }} - {{ employee.phoneNumber }}
            </option>
          </select>
        </div>
        
        <div class="flex space-x-3 space-x-reverse">
          <button 
            @click="closeEmployeeSelectionModal"
            class="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
          >
            انصراف
          </button>
          <button 
            @click="selectEmployeeForInternalOrder"
            class="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            ایجاد سفارش
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api, { sectionsAPI, employeesAPI, ordersAPI } from '@/services/api'
import OrderModal from './OrderModal.vue'

export default {
  name: 'OrderManagement',
  components: {
    OrderModal
  },
  setup() {
    const router = useRouter()
    const sections = ref([])
    const activeSection = ref(null)
    const loading = ref(true)
    const error = ref(null)
        const showOrderModal = ref(false)
    const currentOrder = ref(null)
    const selectedTable = ref(null)
    const showEmployeeSelectionModal = ref(false)
    const employees = ref([])
    const selectedEmployee = ref(null)

    // Pickup and internal orders section
    const pickupSection = ref({
      id: 'pickup',
      name: 'سفارشات تحویل و داخلی',
      tables: []
    })

        const loadSections = async () => {
      try {
        loading.value = true
        error.value = null
        
        // Load regular sections using API
        const sectionsResponse = await sectionsAPI.getAll()
        sections.value = sectionsResponse.data
        
        // Load pickup orders
        await loadPickupOrders()
        
        // Load employees
        await loadEmployees()
        
        // Set active section to pickup orders by default
        activeSection.value = pickupSection.value
      } catch (err) {
        console.error('Error loading sections:', err)
        error.value = 'خطا در بارگذاری بخش‌ها'
      } finally {
        loading.value = false
      }
    }

    const loadEmployees = async () => {
      try {
        const employeesResponse = await employeesAPI.getAll()
        const allEmployees = employeesResponse.data
        employees.value = allEmployees.filter(emp => emp.isActive)
      } catch (err) {
        console.error('Error loading employees:', err)
      }
    }

    const loadPickupOrders = async () => {
      try {
        console.log('Loading pickup orders...')
        // Get pickup and internal orders using API
        const ordersResponse = await ordersAPI.getPickup()
        const orders = ordersResponse.data
        console.log('Pickup and internal orders response:', orders)
        
        // Create virtual tables for all orders
        pickupSection.value.tables = orders.map(order => ({
          id: order.isPickup ? `pickup-${order.id}` : `internal-${order.id}`,
          name: order.isPickup ? `سفارش تحویل #${order.id}` : `سفارش داخلی #${order.id}`,
          description: order.isPickup ? (order.notes || 'سفارش تحویل') : `کارمند: ${order.employeeName || 'نامشخص'}`,
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: order.status,
          openOrderTotal: order.totalAmount,
          order: order
        }))
        console.log('Pickup section tables:', pickupSection.value.tables)
      } catch (err) {
        console.error('Error loading pickup orders:', err)
      }
    }

    const createNewPickupOrder = async () => {
      try {
        // Create a new pickup order
        const createResponse = await api.post('/orders', {
          tableId: null, // null means pickup order
          notes: 'سفارش تحویل',
          isPickup: true,
          deliveryAddress: '',
          deliveryFee: 0
        })
        
        // Refresh pickup orders
        await loadPickupOrders()
        
        // Open the new order
        const newOrder = createResponse.data
        currentOrder.value = newOrder
        selectedTable.value = {
          id: `pickup-${newOrder.id}`,
          name: `سفارش تحویل #${newOrder.id}`,
          description: 'سفارش تحویل',
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: newOrder.status,
          openOrderTotal: newOrder.totalAmount,
          order: newOrder
        }
        showOrderModal.value = true
      } catch (err) {
        console.error('Error creating pickup order:', err)
        alert('خطا در ایجاد سفارش تحویل')
      }
    }

    const createInternalOrder = async () => {
      try {
        // Show employee selection modal first
        showEmployeeSelectionModal.value = true
      } catch (err) {
        console.error('Error creating internal order:', err)
        alert('خطا در ایجاد سفارش داخلی')
      }
    }

    const selectEmployeeForInternalOrder = async () => {
      try {
        if (!selectedEmployee.value) {
          alert('لطفاً یک کارمند انتخاب کنید')
          return
        }

        // Store employee info before clearing
        const employeeName = selectedEmployee.value.fullName
        const employeeId = selectedEmployee.value.id

        // Create a new internal order
        const createResponse = await api.post('/orders', {
          tableId: null,
          notes: `سفارش داخلی - ${employeeName}`,
          isPickup: false,
          isInternalOrder: true,
          employeeId: employeeId,
          deliveryAddress: '',
          deliveryFee: 0
        })
        
        // Close employee selection modal
        showEmployeeSelectionModal.value = false
        selectedEmployee.value = null
        
        // Open the new order
        const newOrder = createResponse.data
        currentOrder.value = newOrder
        selectedTable.value = {
          id: `internal-${newOrder.id}`,
          name: `سفارش داخلی #${newOrder.id}`,
          description: `کارمند: ${employeeName}`,
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: newOrder.status,
          openOrderTotal: newOrder.totalAmount,
          order: newOrder
        }
        showOrderModal.value = true
      } catch (err) {
        console.error('Error creating internal order:', err)
        alert('خطا در ایجاد سفارش داخلی')
      }
    }

    const closeEmployeeSelectionModal = () => {
      showEmployeeSelectionModal.value = false
      selectedEmployee.value = null
    }

    const openTableOrder = async (table) => {
      try {
        selectedTable.value = table
        
        // Check if this is a pickup order
        if (String(table.id).startsWith('pickup-')) {
          // For pickup orders, get the order directly by ID
          const orderId = String(table.id).replace('pickup-', '')
          const response = await api.get(`/orders/${orderId}`)
          currentOrder.value = response.data
        } else if (String(table.id).startsWith('internal-')) {
          // For internal orders, get the order directly by ID
          const orderId = String(table.id).replace('internal-', '')
          const response = await api.get(`/orders/${orderId}`)
          currentOrder.value = response.data
        } else {
          // For regular tables, try to get existing open order
          try {
            const response = await api.get(`/orders/table/${table.id}`)
            currentOrder.value = response.data
          } catch (err) {
            if (err.response?.status === 404) {
              // No open order exists, create new one
              const createResponse = await api.post('/orders', {
                tableId: table.id,
                notes: ''
              })
              currentOrder.value = createResponse.data
            } else {
              throw err
            }
          }
        }
        
        showOrderModal.value = true
      } catch (err) {
        console.error('Error opening table order:', err)
        alert('خطا در باز کردن سفارش')
      }
    }

    const closeOrderModal = () => {
      showOrderModal.value = false
      currentOrder.value = null
      selectedTable.value = null
    }

         const handleOrderUpdated = () => {
       // Refresh sections to update table status
       loadSections()
       
       // If pickup section is active, refresh pickup and internal orders specifically
       if (activeSection.value?.id === 'pickup') {
         loadPickupOrders()
       }
     }

    const goToAdminPanel = () => {
      router.push('/admin')
    }

    const goToMenu = () => {
      router.push('/')
    }

    const goToSectionTableManagement = () => {
      router.push('/admin/sections-tables')
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
    }

    onMounted(() => {
      loadSections()
    })

    return {
      sections,
      activeSection,
      loading,
      error,
      showOrderModal,
      currentOrder,
      selectedTable,
      pickupSection,
      showEmployeeSelectionModal,
      employees,
      selectedEmployee,
      loadSections,
      openTableOrder,
      closeOrderModal,
      handleOrderUpdated,
      createNewPickupOrder,
      createInternalOrder,
      selectEmployeeForInternalOrder,
      closeEmployeeSelectionModal,
      goToAdminPanel,
      goToMenu,
      goToSectionTableManagement,
      formatPrice
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
